import type { IhightLightInfo, IhightLightItem, IhightLightObj, IPanelData, IPosItem } from './ctype';

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)
);

const isSafeKey = (key: string) => !['__proto__', 'constructor', 'prototype'].includes(key);

const parsePanelData = (value: unknown): IPanelData | null => {
  if (!isPlainObject(value)) return null;
  return {
    title: typeof value.title === 'string' ? value.title : '',
    content: typeof value.content === 'string' ? value.content : '',
    timestamp: Number.isFinite(value.timestamp) ? value.timestamp as number : 0,
  };
};

const parsePosition = (value: unknown, uid: string, hasPanel: boolean): IPosItem | null => {
  if (!isPlainObject(value) || typeof value.dataKey !== 'string' || !value.dataKey) return null;
  if (!value.dataKey.replace(/^[$~]/, '').split('.').every(isSafeKey)) return null;
  const startOffset = value.startOffset;
  const endOffset = value.endOffset;
  if (typeof startOffset !== 'number' || typeof endOffset !== 'number' || !Number.isInteger(startOffset) || !Number.isInteger(endOffset) || startOffset < 0 || endOffset <= startOffset) return null;
  return {
    dataKey: value.dataKey,
    startOffset,
    endOffset,
    uid,
    hasPanel,
    ...(typeof value.className === 'string' ? {className: value.className} : {}),
    ...(typeof value.tagName === 'string' ? {tagName: value.tagName} : {}),
  };
};

const parseHighlight = (key: string, value: unknown): IhightLightItem | null => {
  if (!isPlainObject(value)) return null;
  const uid = typeof value.uid === 'string' && value.uid ? value.uid : key;
  const panelData = parsePanelData(value.panelData);
  const posObj = Object.entries(isPlainObject(value.posObj) ? value.posObj : {}).reduce<Record<string, IPosItem[]>>((all, [, positions]) => {
    if (!Array.isArray(positions)) return all;
    positions.forEach(position => {
      const parsed = parsePosition(position, uid, Boolean(panelData));
      if (!parsed) return;
      if (!all[parsed.dataKey]) all[parsed.dataKey] = [];
      all[parsed.dataKey].push(parsed);
    });
    return all;
  }, {});
  const posArr = Object.values(posObj).flat();
  // Keep the student note even when a historical selection cannot be restored.
  if (!posArr.length && !panelData) return null;

  return {
    uid,
    posObj,
    posArr,
    panelData,
    ...(Number.isFinite(value.createdAt) ? {createdAt: value.createdAt as number} : {}),
  };
};

export function parseDraftHighlights(mark: unknown): IhightLightInfo {
  if (typeof mark !== 'string' || !mark) return {};

  let root: unknown;
  try {
    root = JSON.parse(mark);
  } catch {
    return {};
  }
  if (!isPlainObject(root)) return {};

  return Object.entries(root).reduce<Record<string, IhightLightObj>>((pages, [pageIndex, page]) => {
    if (!Number.isInteger(Number(pageIndex)) || Number(pageIndex) < 0 || !isPlainObject(page)) return pages;
    const highlights = Object.entries(page).reduce<IhightLightObj>((all, [key, item]) => {
      if (!isSafeKey(key)) return all;
      const parsed = parseHighlight(key, item);
      if (parsed) all[key] = parsed;
      return all;
    }, {});
    if (Object.keys(highlights).length) pages[pageIndex] = highlights;
    return pages;
  }, {});
}
